import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Map as GoogleMap, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { Clock, Home, MapPinned, Phone, Wrench } from 'lucide-react';
import { BUSINESS } from '../config/business';
import { MapExpertAvatarPin } from './maps';
import { APP_CONFIG } from '../config/app';
import { getExpertAvatarUrl, resolveMapMarkerIconUrl } from '../utils/imageUtils';
import { COVERAGE_TOWNS } from '../data/coverageTowns';
import { fetchJson } from '../utils/fetchJson';

import TrackedContactLink from './TrackedContactLink';
import { getServiceFromPath } from '../utils/serviceFromPath';
import { Link, useLocation } from 'react-router-dom';
type Freelancer = {
  _id: string;
  firstName: string;
  lastName: string;
  role?: string;
  profileImage?: string;
  city?: string;
  postalCode?: string;
};

type LatLng = { lat: number; lng: number };

const CoverageContactSection: React.FC = () => {
  const { pathname } = useLocation();
  const googleMapsMapId = APP_CONFIG.googleMapsMapId;

  const OVERLAP_OFFSETS: Array<Pick<LatLng, 'lat' | 'lng'>> = useMemo(
    () => [
      { lat: 0, lng: 0 },
      { lat: 0.00018, lng: 0 },
      { lat: -0.00018, lng: 0 },
      { lat: 0, lng: 0.00018 },
      { lat: 0, lng: -0.00018 },
      { lat: 0.00018, lng: 0.00018 },
      { lat: -0.00018, lng: -0.00018 },
      { lat: 0.00018, lng: -0.00018 },
      { lat: -0.00018, lng: 0.00018 },
    ],
    []
  );

  const defaultCenter = useMemo<LatLng>(
    () => ({
      lat: 46.603354, // Centre de la France
      lng: 1.888334,
    }),
    []
  );

  // Track whether the Maps API is ready (window.google available)
  const [mapsReady, setMapsReady] = useState(() => Boolean((window as { google?: unknown }).google));

  const [freelancers, setFreelancers] = useState<Freelancer[]>([]);
  const [markers, setMarkers] = useState<Array<{ freelancer: Freelancer; position: LatLng }>>([]);
  const [mapCenter, setMapCenter] = useState<LatLng>(defaultCenter);
  const [selected, setSelected] = useState<{ freelancer: Freelancer; position: LatLng } | null>(null);

  const displayMarkers = useMemo(() => {
    // Si plusieurs freelancers partagent exactement les mêmes coordonnées,
    // ils peuvent être superposés visuellement => on décale légèrement.
    const posToItems = new Map<string, Array<{ idx: number; item: { freelancer: Freelancer; position: LatLng } }>>();

    markers.forEach((item, idx) => {
      const key = `${item.position.lat.toFixed(5)},${item.position.lng.toFixed(5)}`;
      const prev = posToItems.get(key) || [];
      prev.push({ idx, item });
      posToItems.set(key, prev);
    });

    const result: Array<{ freelancer: Freelancer; position: LatLng }> = [];

    markers.forEach((item, originalIdx) => {
      const key = `${item.position.lat.toFixed(5)},${item.position.lng.toFixed(5)}`;
      const group = posToItems.get(key) || [];
      const inGroupIdx = group.findIndex(g => g.idx === originalIdx);

      if (group.length <= 1 || inGroupIdx < 0) {
        result.push(item);
        return;
      }

      const offset = OVERLAP_OFFSETS[inGroupIdx % OVERLAP_OFFSETS.length];
      result.push({
        freelancer: item.freelancer,
        position: {
          lat: item.position.lat + offset.lat,
          lng: item.position.lng + offset.lng,
        },
      });
    });

    return result;
  }, [markers, OVERLAP_OFFSETS]);

  // Poll until window.google is available (loaded by APIProvider)
  useEffect(() => {
    if (mapsReady) return;
    const id = setInterval(() => {
      if ((window as { google?: unknown }).google) {
        setMapsReady(true);
        clearInterval(id);
      }
    }, 100);
    return () => clearInterval(id);
  }, [mapsReady]);

  // Récupère les freelancers (endpoint public via /api/users)
  useEffect(() => {
    let cancelled = false;

    const fetchFreelancers = async () => {
      try {
        const data = await fetchJson<{ users?: Freelancer[] }>(
          '/api/users?role=fondateur,freelancer,freelancer_admin'
        );
        if (cancelled) return;
        setFreelancers(data.users || []);
      } catch (err) {
        console.error('[CoverageContactSection]', err);
        if (!cancelled) setFreelancers([]);
      }
    };

    fetchFreelancers();

    return () => {
      cancelled = true;
    };
  }, []);

  // Géocode côté frontend => le modèle User ne stocke pas de lat/lng
  useEffect(() => {
    if (!mapsReady) return;

    const googleObj = (window as { google?: { maps?: { Geocoder?: new () => google.maps.Geocoder } } })?.google;
    const GeocoderCtor = googleObj?.maps?.Geocoder;
    if (!GeocoderCtor) return;
    if (freelancers.length === 0) return;

    let cancelled = false;
    const geocoder = new GeocoderCtor();

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const geocodeAddress = (
      address: string
    ): Promise<{ coords: LatLng | null; status: string }> => {
      return new Promise(resolve => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results?.[0]?.geometry?.location) {
            const loc = results[0].geometry.location;
            resolve({ coords: { lat: loc.lat(), lng: loc.lng() }, status });
          } else {
            resolve({ coords: null, status });
          }
        });
      });
    };

    const geocodeWithRetry = async (
      address: string,
      retries: number
    ): Promise<{ coords: LatLng | null; status: string }> => {
      let last = { coords: null as LatLng | null, status: 'UNKNOWN_ERROR' };
      for (let attempt = 0; attempt <= retries; attempt++) {
        // Petit délai progressif en cas de soucis temporaires
        if (attempt > 0) await delay(350 * attempt);
        const res = await geocodeAddress(address);
        last = res;
        if (res.coords) return res;

        // Statuts qui peuvent être temporaires => on retente
        if (res.status === 'OVER_QUERY_LIMIT' || res.status === 'UNKNOWN_ERROR') {
          continue;
        }

        // Pour ZERO_RESULTS / INVALID_REQUEST, inutile de retenter
        break;
      }
      return last;
    };

    const keyFor = (f: Freelancer) => `${(f.postalCode || '').trim()}|${(f.city || '').trim()}`;
    const toAddress = (postalCode?: string, city?: string) => {
      const pc = (postalCode || '').trim();
      const c = (city || '').trim();
      if (pc && c) return `${pc} ${c} France`;
      if (c) return `${c} France`;
      return '';
    };

    const run = async () => {
      try {
        const uniqueKeys = Array.from(new Set(freelancers.map(keyFor))).filter(k => {
          const [postalCode, city] = k.split('|');
          return Boolean(postalCode) || Boolean(city);
        });

        const coordsByKey = new Map<string, LatLng>();
        // Géocoder séquentiellement pour limiter les risques de quota/burst.
        for (const k of uniqueKeys) {
          if (cancelled) return;
          const [postalCode, city] = k.split('|');
          const address1 = toAddress(postalCode, city);
          if (!address1) continue;

          // 1) Priorité : code postal + ville + France
          let { coords } = await geocodeWithRetry(address1, 2);

          // 2) Fallback : seulement ville + France (si le code postal est inexistant/ambigu)
          const cityOnly = city ? `${city} France` : '';
          if (!coords && cityOnly && cityOnly !== address1) {
            const fallback = await geocodeWithRetry(cityOnly, 2);
            coords = fallback.coords;
          }

          if (coords) coordsByKey.set(k, coords);
          await delay(250);
        }

        if (cancelled) return;

        const enriched = freelancers
          .map(f => {
            const k = keyFor(f);
            const position = coordsByKey.get(k);
            if (!position) return null;
            return { freelancer: f, position };
          })
          .filter(Boolean) as Array<{ freelancer: Freelancer; position: LatLng }>;

        if (enriched.length > 0) setMapCenter(enriched[0].position);
        setMarkers(enriched);
      } catch (err) {
        console.error('[CoverageContactSection][geocode] ', err);
        if (!cancelled) setMarkers([]);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [freelancers, mapsReady]);

  return (
    <section id="zone-intervention" className="relative bg-canvas border-t border-line section-spacing overflow-hidden">
      <div className="relative container-site">
        <motion.div
          className="card p-5 md:p-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="mb-6 md:mb-8">
            <div className="section-subtitle-badge !mb-4">
              <MapPinned size={14} />
              <span>Zone d’intervention</span>
            </div>
            <h2 className="heading-section mb-3">
              Près de chez vous, <span className="text-accent-soft">autour de Genève</span>
            </h2>
            <p className="text-lead !max-w-none">
              Intervention à domicile ou sur site à Viry et jusqu’à 50&nbsp;km autour de Genève (Pays de Gex, <span className="whitespace-nowrap">Haute-Savoie</span>).
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <div className="relative rounded-xl border border-line bg-surface-2 min-h-[320px] overflow-hidden">
              {APP_CONFIG.googleMapsApiKey ? (
                <GoogleMap
                  style={{ width: '100%', height: '100%', minHeight: '320px' }}
                  defaultCenter={mapCenter}
                  center={mapCenter}
                  defaultZoom={6}
                  mapTypeControl={false}
                  streetViewControl={false}
                  fullscreenControl={false}
                  clickableIcons={false}
                  mapId={googleMapsMapId || undefined}
                >
                  {displayMarkers.map(m => {
                    const imgUrl = resolveMapMarkerIconUrl(getExpertAvatarUrl(m.freelancer.profileImage));
                    return (
                      <AdvancedMarker
                        key={m.freelancer._id}
                        position={m.position}
                        title={m.freelancer.firstName}
                        onClick={() => setSelected(m)}
                      >
                        <MapExpertAvatarPin
                          src={imgUrl}
                          alt={m.freelancer.firstName}
                        />
                      </AdvancedMarker>
                    );
                  })}
                  {selected && (
                    <InfoWindow
                      position={selected.position}
                      onCloseClick={() => setSelected(null)}
                    >
                      <div className="p-2 min-w-[180px]">
                        <div className="flex items-center space-x-2">
                          <img
                            src={resolveMapMarkerIconUrl(getExpertAvatarUrl(selected.freelancer.profileImage))}
                            alt={selected.freelancer.firstName}
                            className="w-10 h-10 rounded-full"
                          />
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {selected.freelancer.firstName}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {[selected.freelancer.postalCode, selected.freelancer.city]
                                .filter(Boolean)
                                .join(' ')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              ) : (
                <iframe
                  title="Zone d'intervention SOS IT Services"
                  src="https://www.google.com/maps?q=Geneve%20France&z=9&output=embed"
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              )}
            </div>

            <div className="flex flex-col gap-4">
              <div className="rounded-xl border border-line bg-surface-2 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line bg-surface text-accent-soft">
                    <Wrench className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="kicker mb-2">Réparation sur place</p>
                    <p className="text-fg font-semibold text-[17px]">Téléphone, PC &amp; Mac</p>
                    <p className="text-fg-muted text-sm mt-1">Diagnostic, prix annoncé, puis intervention chez vous.</p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-line bg-surface-2 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line bg-surface text-accent-soft">
                    <Home className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="kicker mb-2">À domicile ou sur site</p>
                    <p className="text-fg font-semibold text-[17px]">Genève + 50&nbsp;km</p>
                    <p className="text-fg-muted text-sm mt-1">Pays de Gex, <span className="whitespace-nowrap">Haute-Savoie</span> et alentours.</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-line bg-surface-2 p-4">
                <p className="kicker mb-3">Communes desservies</p>
                <ul className="flex flex-wrap gap-2">
                  {COVERAGE_TOWNS.map((town) => (
                    <li key={town.name}>
                      {town.to ? (
                        <Link
                          to={town.to}
                          className="badge-mono inline-flex min-h-11 items-center md:min-h-8 transition-colors hover:text-accent-soft"
                        >
                          {town.name}
                        </Link>
                      ) : (
                        <span className="badge-mono inline-flex min-h-11 items-center md:min-h-8">{town.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-sm text-fg-muted">
                  J’interviens aussi côté Genève (Suisse).
                  {/* TODO(Lucas): moyens de paiement côté Suisse (CHF, TWINT…) à confirmer avant de les mentionner */}
                </p>
              </div>

              <div className="mt-auto flex flex-col gap-3 border-t border-line pt-4">
                <p className="flex items-center gap-3 text-sm text-fg-muted">
                  <Clock size={16} className="text-accent-soft flex-shrink-0" aria-hidden="true" />
                  <span>{BUSINESS.responseLabel}</span>
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                  <Link to={APP_CONFIG.routes.contact} className="btn-primary">
                    Me contacter
                  </Link>
                  <TrackedContactLink channel="phone" service={getServiceFromPath(pathname)} ctaLocation="coverage" href={`tel:${BUSINESS.phoneTel}`} className="price text-fg hover:text-accent-soft transition-colors whitespace-nowrap inline-flex items-center justify-center gap-2 min-h-11">
                    <Phone size={16} className="text-accent-soft" aria-hidden="true" />
                    {BUSINESS.phoneDisplay}
                  </TrackedContactLink>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CoverageContactSection;
